import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SearchBar from "@/components/SearchBar";
import ListingCard from "@/components/ListingCard";
import MapView from "@/components/MapView";
import CategoryBar from "@/components/CategoryBar";
import WishlistPickerModal from "@/components/WishlistPickerModal";
import { MapPin } from "lucide-react";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const region = params.get("region") || "";
  const city = params.get("city") || "";
  const guests = params.get("guests") || "";
  const adults = params.get("adults") || "";
  const children = params.get("children") || "";
  const infants = params.get("infants") || "";
  const pets = params.get("pets") || "";
  const category = params.get("category") || "";

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [picker, setPicker] = useState(null);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (region) q.region = region;
    if (city) q.city = city;
    if (guests) q.guests = guests;
    if (adults) q.adults = adults;
    if (children) q.children = children;
    if (infants) q.infants = infants;
    if (pets) q.pets = pets;
    if (category) q.category = category;
    api.get("/listings", { params: q })
      .then(({ data }) => setListings(data))
      .finally(() => setLoading(false));
  }, [region, city, guests, adults, children, infants, pets, category]);

  const setCategory = (key) => {
    const np = new URLSearchParams(params);
    if (key) np.set("category", key); else np.delete("category");
    setParams(np);
  };

  // Map points
  const points = useMemo(() => {
    if (!listings.length) return [];
    return listings.map((l) => ({ id: l.id, lat: l.lat, lng: l.lng, title: l.title, price: l.price_per_night }));
  }, [listings]);

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-[#FAFAFA]">
      {/* Sticky search */}
      <div className="sticky top-16 sm:top-20 z-30 bg-white/95 backdrop-blur-xl border-b border-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <SearchBar compact defaultValues={{ region, city, guests, adults, children, infants, pets }} />
        </div>
        <CategoryBar value={category} onChange={setCategory} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Listings (60%) */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                {loading ? "Recherche…" : `${listings.length} logement${listings.length > 1 ? "s" : ""} en Bretagne`}
              </h1>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="aspect-[4/5] bg-neutral-200 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-neutral-500">Aucun logement trouvé. Élargissez vos critères.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                {listings.map((l) => (
                  <div
                    key={l.id}
                    onMouseEnter={() => setActiveId(l.id)}
                    onMouseLeave={() => setActiveId(null)}
                  >
                    <ListingCard listing={l} onWishlistClick={(lst) => setPicker(lst)} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Map (40%) */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-[220px]">
              <MapView
                listings={listings}
                activeId={activeId}
                onMarkerClick={setActiveId}
                height={600}
              />
              <p className="mt-3 text-xs text-neutral-500 italic flex items-center gap-2">
                <MapPin className="w-3 h-3" /> Carte interactive · cliquez sur un prix pour voir l'annonce
              </p>
            </div>
          </div>
        </div>
      </div>

      {picker && <WishlistPickerModal listing={picker} onClose={() => setPicker(null)} />}
    </div>
  );
}
